import { useFiltersStore } from '#stores/filters.store';
import { useUserStore } from '#stores/user.store';
import { getFilterTypeAndKeyFromNodeId, type AllFilterType } from '@carrefour-gcs/shared';
import { storeToRefs } from 'pinia';
import { computed, type ComputedRef } from 'vue';
import type { FilterNameType } from '../kpisPerimeter.model';

type FilterType = {
  sectorFilters: string[];
  departmentFilters: string[];
  groupClassFilters: string[];
  classFilters: string[];
  subClassFilters: string[];
  storeFilters: string[];
  chainTypeFilters: string[];
  storesRegionFilters: string[];
  activityTypeFilters: string[];
  integratedFranchisedFilters: string[];
  triptyqueFilters: string[];
  bemHoldingFilters: string[];
  bemMerchandiseFilters: string[];
  brandFilters: string[];
  brandDescFilters: string[];
  subSupplierFilters: string[];
  localSupplierFilters: string[];
  itemFilters: string[];
  barcodeFilters: string[];
  assortmentTypologyFilters: string[];
  productTypologyFilters: string[];
  competitorFilters: string[];
  competitorBaseBarcodeFilters: string[];
  competitorBaseItemFilters: string[];
  competitorComparisonBarcodeFilters: string[];
  competitorComparisonItemFilters: string[];
  competitorBrandsFilters: string[];
  consostyleMicroFilters: string[];
  lifestageMicroFilters: string[];
  businessBrandFilters: string[];
  businessBrandReducedFilters: string[];
};

export const useGetFilters = (): ComputedRef<FilterType> => {
  const userStore = useUserStore();
  const { defaultKpisPerimeter } = storeToRefs(userStore);
  const filtersStore = useFiltersStore();

  const { filters } = storeToRefs(filtersStore);

  const getComputedFilters = (filterKey: FilterNameType, type: AllFilterType) => {
    const selectedFilterValue = filters.value.selectedFilters?.[filterKey];

    const queryValue =
      selectedFilterValue === undefined ? defaultKpisPerimeter.value.filters[filterKey] : selectedFilterValue;

    if (queryValue === null) {
      return [];
    }

    return queryValue.filter((nodeId) => getFilterTypeAndKeyFromNodeId(nodeId).filterType === type);
  };

  return computed(() => {
    return {
      sectorFilters: getComputedFilters('nomenclatureFilters', 'sector'),
      departmentFilters: getComputedFilters('nomenclatureFilters', 'department'),
      groupClassFilters: getComputedFilters('nomenclatureFilters', 'grpClass'),
      classFilters: getComputedFilters('nomenclatureFilters', 'class'),
      subClassFilters: getComputedFilters('nomenclatureFilters', 'subClass'),
      storeFilters: getComputedFilters('storesFilters', 'store'),
      chainTypeFilters: getComputedFilters('storesFilters', 'chainType'),
      activityTypeFilters: getComputedFilters('activityTypesFilters', 'activityType'),
      integratedFranchisedFilters: getComputedFilters('integratedFranchisedFilters', 'integratedFranchised'),
      triptyqueFilters: getComputedFilters('triptyqueFilters', 'brandType'),
      bemHoldingFilters: getComputedFilters('bemHoldingFilters', 'bemMainHolding'),
      bemMerchandiseFilters: getComputedFilters('bemMerchandiseFilters', 'bemMerchandise'),
      brandFilters: getComputedFilters('brandsFilters', 'brand'),
      brandDescFilters: getComputedFilters('brandsFilters', 'brandDesc'),
      subSupplierFilters: getComputedFilters('suppliersFilters', 'subSupplier'),
      localSupplierFilters: getComputedFilters('suppliersFilters', 'localSupplier'),
      barcodeFilters: getComputedFilters('barcodeFilters', 'barcode'),
      itemFilters: getComputedFilters('itemFilters', 'item'),
      storesRegionFilters: getComputedFilters('storesRegionFilters', 'region'),
      assortmentTypologyFilters: getComputedFilters('assortmentTypologyFilters', 'assortmentTypology'),
      productTypologyFilters: getComputedFilters('productTypologyFilters', 'productTypology'),
      competitorFilters: getComputedFilters('competitorFilters', 'competitor'),
      competitorBaseBarcodeFilters: getComputedFilters('competitorBaseBarcodeFilters', 'competitorBaseBarcode'),
      competitorBaseItemFilters: getComputedFilters('competitorBaseItemFilters', 'competitorBaseItem'),
      competitorComparisonBarcodeFilters: getComputedFilters(
        'competitorComparisonBarcodeFilters',
        'competitorComparisonBarcode'
      ),
      competitorComparisonItemFilters: getComputedFilters(
        'competitorComparisonItemFilters',
        'competitorComparisonItem'
      ),
      competitorBrandsFilters: getComputedFilters('competitorBrandsFilters', 'brand'),
      consostyleMicroFilters: getComputedFilters('consostyleMicroFilters', 'consostyleMicro'),
      lifestageMicroFilters: getComputedFilters('lifestageMicroFilters', 'lifestageMicro'),
      businessBrandFilters: getComputedFilters('businessBrandFilters', 'businessBrand'),
      businessBrandReducedFilters: getComputedFilters('businessBrandFilters', 'businessBrandReduced')
    };
  });
};
